import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Info, ShoppingCart } from 'lucide-react';
import { useCart } from '@/context/CartContext';

const BASE = process.env.REACT_APP_BACKEND_URL;
let cached = null;

const load = () => {
  if (!cached) cached = fetch(`${BASE}/api/sales/card-eligibility`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  return cached;
};

export const usePaymentMode = () => {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    let live = true;
    load().then((data) => { if (live) setMode(data ? { test_mode: data.test_mode, live_payments: data.live_payments } : null); });
    return () => { live = false; };
  }, []);
  return mode;
};

export const useCardEligibility = (key, kind = 'products') => {
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    load().then((data) => { if (live) setState(data ? { ...(data[kind]?.[key] || {}), test_mode: data.test_mode } : null); });
    return () => { live = false; };
  }, [key, kind]);
  return state;
};

export const CardOrQuote = ({ slug, kind = 'products', quoteName, image, includes, cardTestId, quoteTestId, dark = false, hideCard = false, className = '' }) => {
  const entry = useCardEligibility(slug, kind);
  const cart = useCart();
  const eligible = Boolean(entry?.card_payable);
  const cartKind = kind === 'packs' ? 'pack' : 'product';
  const addToCart = () => {
    cart.add({ kind: cartKind, id: slug, name: quoteName, image: image || null, includes: includes || null, quantity: 1 });
  };
  const quoteTone = dark ? 'bg-ei-gold text-ei-navy hover:bg-[#b89747]' : 'bg-ei-navy text-white hover:bg-ei-navy/90';
  const activeTone = dark ? 'bg-white text-ei-navy hover:bg-white/90' : 'bg-ei-golddark text-white hover:bg-ei-golddark/90';
  const mutedTone = dark ? 'border border-ei-white/30 text-ei-white/55 cursor-not-allowed' : 'border border-ei-mist text-ei-graphite/50 cursor-not-allowed';
  const note = !eligible
    ? 'Quote only for now. ' + (entry?.reason || 'Card payment is not available for this product yet.')
    : 'Add it to your cart, then pay by card in the cart. Requesting a quote is always available.';
  const shared = 'flex-1 inline-flex items-center justify-center gap-2 rounded-sm px-6 py-4 font-display text-sm font-bold min-h-[44px] transition-colors';
  return <div className={className} data-testid={`${cardTestId}-group`}>
    <div className="flex flex-col sm:flex-row gap-3">
      {hideCard ? null : eligible ? (
        <button type="button" onClick={addToCart} className={`${shared} ${activeTone}`} data-testid={cardTestId}><ShoppingCart className="w-4 h-4" /> Add to cart</button>
      ) : (
        <button type="button" disabled aria-disabled="true" className={`${shared} ${mutedTone}`} data-testid={cardTestId}><ShoppingCart className="w-4 h-4" /> Quote only for now</button>
      )}
      <Link to={`/request-a-quote?product=${encodeURIComponent(quoteName)}`} className={`${shared} ${quoteTone}`} data-testid={quoteTestId}>
        Request a trade quote <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
    {note && <p className={`mt-3 flex gap-2 text-xs leading-relaxed ${dark ? 'text-ei-white/60' : 'text-ei-graphite/65'}`} data-testid={`${cardTestId}-reason`}>
      <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>{note}</span>
    </p>}
  </div>;
};
